import {
  WorkflowMetaData,
  WorkflowStatusMetaData,
  WorkflowSubStatusMetaData,
} from "@finpay-development/shared-platform/lib/esm/meta-data/models/metaData";
import { PatientEncounterCard } from "@finpay-development/shared-platform/lib/esm/patient/components/models/patient-encounter-card";
import { WorkflowId } from "@finpay-development/shared-platform/lib/esm/patient/components/misc/patient-status";
import { patientHelper } from "@finpay-development/shared-platform/lib/esm/patient/services/patient-service";

/** Hardcoded FinAdvisor workflow id for the patient-records board (workflowId = 2). */
export const BOARD_WORKFLOW_ID = 2;

export const FALLBACK_SUBSTATUS = {
  workflowSubStatusId: 7,
  workflowSubStatusName: "None",
};

/** Full PE status PATCH body shape (ids + names). */
export interface PeStatusPatchBody {
  workflowId: number;
  workflowStatus: {
    workflowStatusId: number;
    workflowStatusName: string;
  };
  workflowSubStatus: {
    workflowSubStatusId: number;
    workflowSubStatusName: string;
  };
}

export function getBoardWorkflowStatuses(
  workflows: WorkflowMetaData[] | undefined | null
): WorkflowStatusMetaData[] {
  if (!Array.isArray(workflows)) {
    return [];
  }
  const boardWorkflow = workflows.find(
    (wf) => Number(wf?.workflowId) === BOARD_WORKFLOW_ID
  );
  const workflowStatus = boardWorkflow?.workflowStatus;
  const statuses = Array.isArray(workflowStatus) ? [...workflowStatus] : [];

  return statuses
    .filter((st) => Number(st?.workflowStatusId) > 0)
    .sort((a, b) => {
      const orderA = Number(
        (a as WorkflowStatusMetaData & { processOrder?: number }).processOrder
      );
      const orderB = Number(
        (b as WorkflowStatusMetaData & { processOrder?: number }).processOrder
      );
      const hasA = Number.isFinite(orderA);
      const hasB = Number.isFinite(orderB);
      if (hasA && hasB && orderA !== orderB) {
        return orderA - orderB;
      }
      if (hasA && !hasB) {
        return -1;
      }
      if (!hasA && hasB) {
        return 1;
      }
      return Number(a.workflowStatusId) - Number(b.workflowStatusId);
    });
}

export function getColumnLabel(status: WorkflowStatusMetaData): string {
  return (
    (status.statusName && String(status.statusName).trim()) ||
    (status.workflowStatusDesc && String(status.workflowStatusDesc).trim()) ||
    `Status ${status.workflowStatusId}`
  );
}

export function getBoardStatusIdsJoined(
  statuses: WorkflowStatusMetaData[]
): string {
  return statuses
    .map((st) => Number(st.workflowStatusId))
    .filter((id) => Number.isFinite(id) && id > 0)
    .join("||");
}

export function getSubStatusLabel(sub: WorkflowSubStatusMetaData): string {
  const named = sub as WorkflowSubStatusMetaData & {
    subStatusName?: string;
    workflowSubStatusName?: string;
  };
  return (
    (named.subStatusName && String(named.subStatusName).trim()) ||
    (named.workflowSubStatusName &&
      String(named.workflowSubStatusName).trim()) ||
    (named.workflowSubStatusDesc &&
      String(named.workflowSubStatusDesc).trim()) ||
    FALLBACK_SUBSTATUS.workflowSubStatusName
  );
}

/** Max substatus landing zones shown per column while dragging. */
export const MAX_SUBSTATUSES_PER_COLUMN = 10;

/** Substatuses for a column, sorted by processOrder when present. */
export function getColumnSubStatuses(
  status: WorkflowStatusMetaData,
  limit: number = MAX_SUBSTATUSES_PER_COLUMN
): WorkflowSubStatusMetaData[] {
  const subs = Array.isArray(status.workflowSubStatus)
    ? status.workflowSubStatus.filter(
        (sub) => Number(sub?.workflowSubStatusId) > 0
      )
    : [];

  const sorted = [...subs].sort((a, b) => {
    const orderA = Number(
      (a as WorkflowSubStatusMetaData & { processOrder?: number }).processOrder
    );
    const orderB = Number(
      (b as WorkflowSubStatusMetaData & { processOrder?: number }).processOrder
    );
    const hasA = Number.isFinite(orderA);
    const hasB = Number.isFinite(orderB);
    if (hasA && hasB && orderA !== orderB) {
      return orderA - orderB;
    }
    if (hasA && !hasB) {
      return -1;
    }
    if (!hasA && hasB) {
      return 1;
    }
    return Number(a.workflowSubStatusId) - Number(b.workflowSubStatusId);
  });

  if (limit > 0) {
    return sorted.slice(0, limit);
  }
  return sorted;
}

/** Build hardcoded-shape PE status PATCH body for a status + substatus landing. */
export function buildStatusPatchBody(
  status: WorkflowStatusMetaData,
  subStatus?: WorkflowSubStatusMetaData | null
): PeStatusPatchBody {
  const statusName = getColumnLabel(status);
  const chosen =
    subStatus && Number(subStatus.workflowSubStatusId) > 0
      ? subStatus
      : getColumnSubStatuses(status)[0];

  const workflowSubStatus = chosen
    ? {
        workflowSubStatusId: Number(chosen.workflowSubStatusId),
        workflowSubStatusName: getSubStatusLabel(chosen),
      }
    : { ...FALLBACK_SUBSTATUS };

  return {
    workflowId: BOARD_WORKFLOW_ID,
    workflowStatus: {
      workflowStatusId: Number(status.workflowStatusId),
      workflowStatusName: statusName,
    },
    workflowSubStatus,
  };
}

export function getCardWorkflowStatusId(
  card: PatientEncounterCard
): number | null {
  const id = Number(card?.workflow?.workflowStatus?.workflowStatusId);
  if (!Number.isFinite(id) || id <= 0) {
    return null;
  }
  return id;
}

export function getCardWorkflowSubStatusId(
  card: PatientEncounterCard
): number | null {
  const id = Number(card?.workflow?.workflowSubStatus?.workflowSubStatusId);
  if (!Number.isFinite(id) || id <= 0) {
    return null;
  }
  return id;
}

export const OTHER_SUBSTATUS_KEY = 0;

/**
 * Group cards under status → substatus.
 * Cards with a status but unknown/missing substatus go under OTHER_SUBSTATUS_KEY (0).
 */
export function groupCardsByStatusAndSubStatus(
  cards: PatientEncounterCard[],
  allowedStatusIds: Set<number>
): Map<number, Map<number, PatientEncounterCard[]>> {
  const grouped = new Map<number, Map<number, PatientEncounterCard[]>>();
  allowedStatusIds.forEach((id) => {
    grouped.set(id, new Map());
  });

  cards.forEach((card) => {
    const statusId = getCardWorkflowStatusId(card);
    if (statusId == null || !allowedStatusIds.has(statusId)) {
      return;
    }
    const bySub = grouped.get(statusId)!;
    const subId = getCardWorkflowSubStatusId(card) ?? OTHER_SUBSTATUS_KEY;
    const bucket = bySub.get(subId) || [];
    bucket.push(card);
    bySub.set(subId, bucket);
  });

  return grouped;
}

/** Apply PE patch body onto a card's local workflow for optimistic UI. */
export function applyPatchBodyToCard(
  card: PatientEncounterCard,
  body: PeStatusPatchBody
): PatientEncounterCard {
  const updated: PatientEncounterCard = {
    ...card,
    workflow: {
      ...card.workflow,
      workflowId: body.workflowId,
      workflowName: card.workflow?.workflowName || "Preconverted",
      workflowStatus: {
        workflowStatusId: body.workflowStatus.workflowStatusId,
        workflowStatusDesc: body.workflowStatus.workflowStatusName,
      },
      workflowSubStatus: {
        workflowSubStatusId: body.workflowSubStatus.workflowSubStatusId,
        workflowSubStatusDesc: body.workflowSubStatus.workflowSubStatusName,
      },
    },
  };

  return patientHelper.setStatusColor([updated], WorkflowId.Preconverted)[0];
}
